import {
  faCalendarAlt,
  faCalendarPlus,
  faCar,
  faCarAlt,
  faCarSide,
  faChartBar,
  faClipboardList,
  faCoins,
  faEllipsisH,
  faExclamationCircle,
  faFile,
  faFileInvoiceDollar,
  faHandshake,
  faIdCard,
  faList,
  faMapPin,
  faMoneyBill,
  faMoneyCheck,
  faReceipt,
  faRedo,
  faSearch,
  faSignOutAlt,
  faStream,
  faTag,
  faTags,
  faToolbox,
  faTrash,
  faUser,
  faUserCheck,
  faUserCircle,
  faUserTie,
  faUsers,
  faMinus,
  faDollarSign,
  faMobileAlt,
  faStar
  // faUsers,
  // faMobile
} from '@fortawesome/free-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import DepartureBoardIcon from '@material-ui/icons/DepartureBoard';
import NotInterestedIcon from '@material-ui/icons/NotInterested';
import { useSuperQuery } from '@vadiun/react-hooks-legacy';
import { UserRole } from 'app/user/models/User';
import { useTransferReservationToValidateCount } from 'app/viaje/viajeTraslado/pendienteValidacion/context/TransferReservationToValidateCountContext';
import { useViajesTraslado } from 'app/viaje/services';
import { useAuth } from 'auth/services/AuthService';
import { useEffect, useState } from 'react';
import { useHistory } from 'react-router-dom';
import { useThemeContext } from 'shared/context/themeContext';
import { dark as darkColors, light as lightColors } from 'shared/theme/colorTokens';
import Logo from '../../../assets/logo.png';
import SidebarHeader from './SidebarHeader';
import SidebarItem, { SelectedItemContext } from './SidebarItem';

export type SideBarStatus =
  | {
    platform: 'mobile';
    status: 'open' | 'closed';
  }
  | {
    platform: 'desktop';
    status: 'collapsed' | 'expanded';
  };

interface Props {
  status: SideBarStatus;
  toggleCollapse: () => void;
  totalCancelTrips: number;
}

interface PropsSidebarSection {
  titulo: string;
  isCollapsed: boolean;
}

const SidebarSection = ({ titulo, isCollapsed }: PropsSidebarSection) => {
  return (
    <h1 className="mt-7 mb-4 px-8 text-xs font-semibold text-blueGray-700">
      {!isCollapsed ? titulo : <FontAwesomeIcon icon={faEllipsisH} size="1x" />}
    </h1>
  );
};

const Sidebar = ({ status, toggleCollapse, totalCancelTrips }: Props) => {
  const authService = useAuth();
  const { theme } = useThemeContext();
  const isDarkTheme = theme === 'dark';
  const [selectedItem, setSelectedItem] = useState<string | null>(null);
  const [isTemporarilyExpanded, setIsTemporarilyExpanded] = useState(false);
  const history = useHistory();

  const isCollapsed =
    status.platform === 'desktop' &&
    status.status === 'collapsed' &&
    !isTemporarilyExpanded;

  const { getAmountOfTransferReservationToValidate } = useViajesTraslado();
  const amountOfTransferReservationToValidateQuery = useSuperQuery(
    getAmountOfTransferReservationToValidate,
    { showErrorMessage: false }
  );
  const { invalidateTransferReservationToValidateCount, invalidateVersion } = useTransferReservationToValidateCount();

  useEffect(() => {
    amountOfTransferReservationToValidateQuery.reload();
  }, [invalidateVersion]);

  useEffect(() => {
    const interval = setInterval(
      () => invalidateTransferReservationToValidateCount(),
      60000
    );
    return () => clearInterval(interval);
  }, []);

  useEffect(() => {
    setSelectedItem(history.location.pathname);
    history.listen((hist) => {
      setSelectedItem(hist.pathname);
    });
  }, []);

  const collapsedClass = () => {
    const baseClass =
      ' shadow-md  transition-all duration-300 ease-in-out h-screen ';
    if (status.platform === 'mobile') {
      const mobileBaseClass = `${baseClass} flex-shrink-0 fixed min-h-full `;
      const mobileClosedClass = `${mobileBaseClass} hidden hide-sidebar`;
      return status.status === 'closed' ? mobileClosedClass : mobileBaseClass;
    }
    if (status.platform === 'desktop') {
      const desktopBaseClass = `${baseClass} block show-sidebar relative flex flex-col`;
      return desktopBaseClass;
    }
  };

  const onMouseOver = () => {
    if (status.platform === 'desktop' && status.status === 'collapsed') {
      setIsTemporarilyExpanded(true);
    }
  };

  const onMouseOut = () => {
    if (status.platform === 'desktop' && status.status === 'collapsed') {
      setIsTemporarilyExpanded(false);
    }
  };

  const hasAnyRole = (roles: UserRole[]) =>
    authService?.roles?.some((role) => roles.includes(role));
  // rgb(0, 0, 112)
  return (
    <SelectedItemContext.Provider value={selectedItem}>
      <div
        className={collapsedClass()}
        style={{
          zIndex: 500,
          maxWidth: isCollapsed ? '114px' : '340px',
          boxShadow: isDarkTheme ? darkColors.glow : lightColors.glow
        }}
        onMouseEnter={onMouseOver}
        onMouseLeave={onMouseOut}
      >
        <SidebarHeader
          toggleCollapse={toggleCollapse}
          isCollapsed={isCollapsed}
          isMobile={status.platform === 'mobile'}
          logo={Logo}
        />
        <div
          className="hide-scroll justify-between flex-1 overflow-y-auto pt-2"
          style={{
            background: isDarkTheme
              ? darkColors.sidebarMidGradient
              : lightColors.sidebarMidGradient,
            height: '78px'
          }}
        >
          <div className="flex flex-col gap-y-2">
            {/* OPERACIONES */}
            {hasAnyRole([
              UserRole.OPERADOR_CALL_CENTER,
              UserRole.COORDINADOR,
              UserRole.GERENTE_OPERACIONES,
              UserRole.SUPERVISOR_COORDINACION,
              UserRole.SUPER_ADMIN,
              UserRole.ANALISTA_CUENTAS_A_PAGAR,
              UserRole.GERENTE_ADMINISTRACION,
              UserRole.RESPONSABLE_COBRANZA,
              UserRole.ANALISTA_FACTURACION,
              UserRole.GERENTE_COMERCIAL,
              UserRole.ANALISTA_COMERCIAL,
              UserRole.RESPONSABLE_CHOFERES,
            ]) ? (
              <SidebarItem
                label="OPERACIONES"
                fontSize={16}
                abbreviationLabel="OPERA..."
                isCollapsed={isCollapsed}
                noneIcon={true}
              >
                <SidebarItem
                  label="C.D.R."
                  abbreviationLabel="C.D.R."
                  isCollapsed={isCollapsed}
                  icon={
                    amountOfTransferReservationToValidateQuery?.data?.amount > 0 ? (
                      <div
                        style={{
                          position: 'relative',
                          width: '20px',
                          height: '10px',
                          display: 'flex',
                          justifyContent: 'start',
                          alignItems: 'center',
                          padding: 'none',
                          margin: 'none',
                          marginRight: '-10px'
                        }}
                      >
                        <div
                          style={{
                            backgroundColor: '#5CB3FF',
                            width: '12px',
                            height: '12px',
                            borderRadius: '50%',
                            animation:
                              'ping 1s cubic-bezier(0, 0, 0.5, 0) infinite',
                            position: 'absolute',
                            right: '10px',
                            top: '-1px'
                          }}
                        />{' '}
                        <div
                          style={{
                            backgroundColor: '#0089FF',
                            width: '8px',
                            height: '8px',
                            borderRadius: '50%'
                          }}
                        />
                      </div>
                    ) : null
                  }
                >
                  {hasAnyRole([
                    UserRole.OPERADOR_CALL_CENTER,
                    UserRole.COORDINADOR,
                    UserRole.GERENTE_OPERACIONES,
                    UserRole.SUPERVISOR_COORDINACION,
                    UserRole.SUPER_ADMIN
                  ]) ? (
                    <>
                      <SidebarItem
                        icon={<FontAwesomeIcon icon={faCarAlt} size="1x" />}
                        label="Crear Corporativa"
                        isSelected={
                          selectedItem === '/main/operations/cdr/create-corporate' ||
                          selectedItem?.includes(
                            '/main/operations/cdr/create-corporate'
                          )
                        }
                        redirectTo="/main/operations/cdr/create-corporate"
                        isCollapsed={isCollapsed}
                      />
                      <SidebarItem
                        icon={
                          <div className="flex relative gap-x-0.5">
                            <FontAwesomeIcon icon={faCarAlt} size="1x" />
                            <div
                              className="absolute"
                              style={{ right: '-3px', top: '-7px' }}
                            >
                              <FontAwesomeIcon icon={faCarAlt} size="1x" />
                            </div>
                          </div>
                        }
                        label="Carga Masiva"
                        isSelected={selectedItem === '/main/operations/cdr/bulk-upload'}
                        redirectTo="/main/operations/cdr/bulk-upload"
                        isCollapsed={isCollapsed}
                      />
                      <SidebarItem
                        icon={<FontAwesomeIcon icon={faFile} size="1x" />}
                        label="Borrador Bot"
                        isSelected={selectedItem === '/main/operations/cdr/bot-drafts'}
                        redirectTo="/main/operations/cdr/bot-drafts"
                        isCollapsed={isCollapsed}
                      />
                    </>
                  ) : null}
                  {hasAnyRole([
                    UserRole.OPERADOR_CALL_CENTER,
                    UserRole.COORDINADOR,
                    UserRole.GERENTE_OPERACIONES,
                    UserRole.SUPERVISOR_COORDINACION,
                    UserRole.SUPER_ADMIN
                  ]) ? (
                    <SidebarItem
                      icon={<FontAwesomeIcon icon={faUser} size="1x" />}
                      label="Crear Particular"
                      isSelected={
                        selectedItem === '/main/operations/cdr/create-particular'
                      }
                      redirectTo="/main/operations/cdr/create-particular"
                      isCollapsed={isCollapsed}
                    />
                  ) : null}
                  {hasAnyRole([
                    UserRole.OPERADOR_CALL_CENTER,
                    UserRole.COORDINADOR,
                    UserRole.GERENTE_OPERACIONES,
                    UserRole.SUPERVISOR_COORDINACION,
                    UserRole.SUPER_ADMIN
                  ]) ? (
                    <SidebarItem
                      icon={<FontAwesomeIcon icon={faRedo} size="1x" />}
                      label="Repetitivas Corporativo"
                      isSelected={
                        selectedItem === '/main/operations/cdr/recurring-corporate'
                      }
                      redirectTo="/main/operations/cdr/recurring-corporate"
                      isCollapsed={isCollapsed}
                    />
                  ) : null}
                  {hasAnyRole([
                    UserRole.OPERADOR_CALL_CENTER,
                    UserRole.COORDINADOR,
                    UserRole.GERENTE_OPERACIONES,
                    UserRole.SUPERVISOR_COORDINACION,
                    UserRole.SUPER_ADMIN
                  ]) ? (
                    <SidebarItem
                      icon={<FontAwesomeIcon icon={faRedo} size="1x" />}
                      label="Repetitivas Particular"
                      isSelected={
                        selectedItem === '/main/operations/cdr/recurring-particular'
                      }
                      redirectTo="/main/operations/cdr/recurring-particular"
                      isCollapsed={isCollapsed}
                    />
                  ) : null}
                  {hasAnyRole([
                    UserRole.OPERADOR_CALL_CENTER,
                    UserRole.COORDINADOR,
                    UserRole.GERENTE_OPERACIONES,
                    UserRole.SUPERVISOR_COORDINACION,
                    UserRole.GERENTE_OPERACIONES,
                    UserRole.ANALISTA_CUENTAS_A_PAGAR,
                    UserRole.GERENTE_ADMINISTRACION,
                    UserRole.RESPONSABLE_COBRANZA,
                    UserRole.ANALISTA_FACTURACION,
                    UserRole.GERENTE_COMERCIAL,
                    UserRole.ANALISTA_COMERCIAL,
                    UserRole.SUPER_ADMIN
                  ]) ? (
                    <SidebarItem
                      icon={<FontAwesomeIcon icon={faClipboardList} size="1x" />}
                      label="Gestión Integral"
                      isSelected={
                        selectedItem === '/main/operations/cdr/comprehensive-management'
                      }
                      redirectTo="/main/operations/cdr/comprehensive-management"
                      isCollapsed={isCollapsed}
                    />
                  ) : null}
                  {hasAnyRole([
                    UserRole.COORDINADOR,
                    UserRole.SUPERVISOR_COORDINACION,
                    UserRole.SUPER_ADMIN
                  ]) ? (
                    <SidebarItem
                      icon={<DepartureBoardIcon />}
                      label="Pendientes"
                      isSelected={selectedItem === '/main/operations/coordination/pending-trips'}
                      redirectTo="/main/operations/coordination/pending-trips"
                      isCollapsed={isCollapsed}
                    />
                  ) : null}
                  {hasAnyRole([
                    UserRole.OPERADOR_CALL_CENTER,
                    UserRole.COORDINADOR,
                    UserRole.GERENTE_OPERACIONES,
                    UserRole.SUPERVISOR_COORDINACION,
                    UserRole.SUPER_ADMIN
                  ]) ? (
                    <SidebarItem
                      icon={<FontAwesomeIcon icon={faCarSide} size="1x" />}
                      label="Viajes Abiertos"
                      isSelected={selectedItem === '/main/operations/coordination/open-trips'}
                      redirectTo="/main/operations/coordination/open-trips"
                      isCollapsed={isCollapsed}
                    />
                  ) : null}
                  {hasAnyRole([
                    UserRole.RESPONSABLE_CHOFERES,
                    UserRole.SUPERVISOR_COORDINACION,
                    UserRole.GERENTE_OPERACIONES,
                    UserRole.ANALISTA_CUENTAS_A_PAGAR,
                    UserRole.GERENTE_ADMINISTRACION,
                    UserRole.GERENTE_COMERCIAL,
                    UserRole.ANALISTA_COMERCIAL,
                    UserRole.SUPER_ADMIN
                  ]) ? (
                    <SidebarItem
                      icon={<FontAwesomeIcon icon={faIdCard} size="1x" />}
                      label="Choferes"
                      isSelected={selectedItem === '/main/operations/gdf/drivers'}
                      redirectTo="/main/operations/gdf/drivers"
                      isCollapsed={isCollapsed}
                    />
                  ) : null}

                  {hasAnyRole([
                    UserRole.OPERADOR_CALL_CENTER,
                    UserRole.COORDINADOR,
                    UserRole.GERENTE_OPERACIONES,
                    UserRole.SUPERVISOR_COORDINACION,
                    UserRole.SUPER_ADMIN
                  ]) ? (
                    <SidebarItem
                      icon={
                        (amountOfTransferReservationToValidateQuery?.data?.amount > 0) ? (
                          <div className="relative flex w-6 items-center justify-center rounded-full bg-sidebarText-selected px-1 py-1 text-xs text-black index-10">
                            {amountOfTransferReservationToValidateQuery.data.amount}
                            <div
                              className="absolute"
                              style={{
                                right: '1px',
                                top: '0px',
                                width: '22px',
                                height: '22px',
                                borderRadius: '50%',
                                border: '4px solid #5CB3FF',
                                backgroundColor: 'transparent',
                                animation:
                                  'ping 1s cubic-bezier(0, 0, 0.5, 0) infinite',
                                zIndex: 0,
                              }}
                            ></div>
                          </div>
                        ) : (
                          <NotInterestedIcon />
                        )
                      }
                      label="A Validar App"
                      isSelected={
                        selectedItem === '/main/operations/cdr/app-validation'
                      }
                      redirectTo="/main/operations/cdr/app-validation"
                      isCollapsed={isCollapsed}
                    />
                  ) : null}


                </SidebarItem>

                <SidebarItem
                  label="Coordinación"
                  abbreviationLabel="C."
                  isCollapsed={isCollapsed}
                  icon={
                    totalCancelTrips > 0 ? (
                      <div
                        style={{
                          position: 'relative',
                          width: '20px',
                          height: '10px',
                          display: 'flex',
                          justifyContent: 'start',
                          alignItems: 'center',
                          padding: 'none',
                          margin: 'none',
                          marginRight: '-10px'
                        }}
                      >
                        <div
                          style={{
                            backgroundColor: '#5CB3FF',
                            width: '12px',
                            height: '12px',
                            borderRadius: '50%',
                            animation:
                              'ping 1s cubic-bezier(0, 0, 0.5, 0) infinite',
                            position: 'absolute',
                            right: '10px',
                            top: '-1px'
                          }}
                        />{' '}
                        <div
                          style={{
                            backgroundColor: '#0089FF',
                            width: '8px',
                            height: '8px',
                            borderRadius: '50%'
                          }}
                        />
                      </div>
                    ) : null
                  }
                >
                  {hasAnyRole([
                    UserRole.COORDINADOR,
                    UserRole.SUPERVISOR_COORDINACION,
                    UserRole.SUPER_ADMIN
                  ]) ? (
                    <SidebarItem
                      icon={<DepartureBoardIcon />}
                      label="Pendientes"
                      isSelected={selectedItem === '/main/operations/coordination/pending-trips'}
                      redirectTo="/main/operations/coordination/pending-trips"
                      isCollapsed={isCollapsed}
                    />
                  ) : null}

                  {hasAnyRole([
                    UserRole.OPERADOR_CALL_CENTER,
                    UserRole.COORDINADOR,
                    UserRole.GERENTE_OPERACIONES,
                    UserRole.SUPERVISOR_COORDINACION,
                    UserRole.SUPER_ADMIN
                  ]) ? (
                    <SidebarItem
                      icon={<FontAwesomeIcon icon={faCarSide} size="1x" />}
                      label="Viajes Abiertos"
                      isSelected={selectedItem === '/main/operations/coordination/open-trips'}
                      redirectTo="/main/operations/coordination/open-trips"
                      isCollapsed={isCollapsed}
                    />
                  ) : null}
                  {hasAnyRole([
                    UserRole.OPERADOR_CALL_CENTER,
                    UserRole.COORDINADOR,
                    UserRole.GERENTE_OPERACIONES,
                    UserRole.SUPERVISOR_COORDINACION,
                    UserRole.GERENTE_OPERACIONES,
                    UserRole.ANALISTA_CUENTAS_A_PAGAR,
                    UserRole.GERENTE_ADMINISTRACION,
                    UserRole.RESPONSABLE_COBRANZA,
                    UserRole.ANALISTA_FACTURACION,
                    UserRole.GERENTE_COMERCIAL,
                    UserRole.ANALISTA_COMERCIAL,
                    UserRole.SUPER_ADMIN
                  ]) ? (
                    <SidebarItem
                      icon={<FontAwesomeIcon icon={faClipboardList} size="1x" />}
                      label="Gestión Integral"
                      isSelected={
                        selectedItem === '/main/operations/cdr/comprehensive-management'
                      }
                      redirectTo="/main/operations/cdr/comprehensive-management"
                      isCollapsed={isCollapsed}
                    />
                  ) : null}
                  {hasAnyRole([
                    UserRole.RESPONSABLE_CHOFERES,
                    UserRole.SUPERVISOR_COORDINACION,
                    UserRole.GERENTE_OPERACIONES,
                    UserRole.ANALISTA_CUENTAS_A_PAGAR,
                    UserRole.GERENTE_ADMINISTRACION,
                    UserRole.GERENTE_COMERCIAL,
                    UserRole.ANALISTA_COMERCIAL,
                    UserRole.SUPER_ADMIN
                  ]) ? (
                    <SidebarItem
                      icon={<FontAwesomeIcon icon={faIdCard} size="1x" />}
                      label="Choferes"
                      isSelected={selectedItem === '/main/operations/gdf/drivers'}
                      redirectTo="/main/operations/gdf/drivers"
                      isCollapsed={isCollapsed}
                    />
                  ) : null}
                  {hasAnyRole([
                    UserRole.RESPONSABLE_CHOFERES,
                    UserRole.COORDINADOR,
                    UserRole.GERENTE_OPERACIONES,
                    UserRole.SUPERVISOR_COORDINACION,
                    UserRole.GERENTE_OPERACIONES,
                    UserRole.GERENTE_COMERCIAL,
                    UserRole.SUPER_ADMIN
                  ]) ? (
                    <SidebarItem
                      icon={<FontAwesomeIcon icon={faMoneyCheck} size="1x" />}
                      label="Promedio choferes"
                      isSelected={
                        selectedItem === '/main/operations/driver-operational-information/driver-averages'
                      }
                      redirectTo="/main/operations/driver-operational-information/driver-averages"
                      isCollapsed={isCollapsed}
                    />
                  ) : null}
                  {hasAnyRole([
                    UserRole.COORDINADOR,
                    UserRole.SUPERVISOR_COORDINACION,
                    UserRole.SUPER_ADMIN
                  ]) ? (
                    <SidebarItem
                      icon={
                        totalCancelTrips > 0 ? (
                          <div className="relative flex w-6 items-center justify-center rounded-full bg-sidebarText-selected px-1 py-1 text-xs text-black index-10">
                            {totalCancelTrips}
                            <div
                              className="absolute"
                              style={{
                                right: '1px',
                                top: '0px',
                                width: '22px',
                                height: '22px',
                                borderRadius: '50%',
                                border: '4px solid #5CB3FF',
                                backgroundColor: 'transparent',
                                animation:
                                  'ping 1s cubic-bezier(0, 0, 0.5, 0) infinite',
                                zIndex: 0,
                              }}
                            ></div>
                          </div>
                        ) : (
                          <NotInterestedIcon />
                        )
                      }
                      label="Cancelados App"
                      isSelected={selectedItem === '/main/operations/cdr/app-cancelled'}
                      redirectTo="/main/operations/cdr/app-cancelled"
                      isCollapsed={isCollapsed}
                    />
                  ) : null}
                </SidebarItem>
                <SidebarItem
                  label="GDF"
                  abbreviationLabel="GDF"
                  isCollapsed={isCollapsed}
                >
                  {hasAnyRole([
                    UserRole.RESPONSABLE_CHOFERES,
                    UserRole.SUPERVISOR_COORDINACION,
                    UserRole.GERENTE_OPERACIONES,
                    UserRole.ANALISTA_CUENTAS_A_PAGAR,
                    UserRole.GERENTE_ADMINISTRACION,
                    UserRole.GERENTE_COMERCIAL,
                    UserRole.ANALISTA_COMERCIAL,
                    UserRole.SUPER_ADMIN
                  ]) ? (
                    <SidebarItem
                      icon={<FontAwesomeIcon icon={faIdCard} size="1x" />}
                      label="Choferes"
                      isSelected={selectedItem === '/main/operations/gdf/drivers'}
                      redirectTo="/main/operations/gdf/drivers"
                      isCollapsed={isCollapsed}
                    />
                  ) : null}
                  {hasAnyRole([
                    UserRole.RESPONSABLE_CHOFERES,
                    UserRole.GERENTE_OPERACIONES,
                    UserRole.GERENTE_COMERCIAL,
                    UserRole.ANALISTA_COMERCIAL,
                    UserRole.SUPER_ADMIN
                  ]) ? (
                    <SidebarItem
                      label="Gestión Vehiculos"
                      abbreviationLabel="G.V."
                      isCollapsed={isCollapsed}
                    >
                      <SidebarItem
                        icon={<FontAwesomeIcon icon={faCar} size="1x" />}
                        label="Vehiculos"
                        isSelected={selectedItem === '/main/operations/gdf/vehicle-management/vehicles'}
                        redirectTo="/main/operations/gdf/vehicle-management/vehicles"
                        isCollapsed={isCollapsed}
                      />
                      <SidebarItem
                        icon={<FontAwesomeIcon icon={faUserCheck} size="1x" />}
                        label="Titulares"
                        isSelected={selectedItem === '/main/operations/gdf/vehicle-management/titulares'}
                        redirectTo="/main/operations/gdf/vehicle-management/titulares"
                        isCollapsed={isCollapsed}
                      />
                      <SidebarItem
                        icon={<FontAwesomeIcon icon={faTags} size="1x" />}
                        label="Fabricantes"
                        isSelected={selectedItem === '/main/operations/gdf/vehicle-management/producer'}
                        redirectTo="/main/operations/gdf/vehicle-management/producer"
                        isCollapsed={isCollapsed}
                      />
                      <SidebarItem
                        icon={<FontAwesomeIcon icon={faStream} size="1x" />}
                        label="Modelos"
                        isSelected={selectedItem === '/main/operations/gdf/vehicle-management/models'}
                        redirectTo="/main/operations/gdf/vehicle-management/models"
                        isCollapsed={isCollapsed}
                      />
                    </SidebarItem>
                  ) : null}
                  {hasAnyRole([
                    UserRole.RESPONSABLE_CHOFERES,
                    UserRole.SUPERVISOR_COORDINACION,
                    UserRole.GERENTE_OPERACIONES,
                    UserRole.SUPER_ADMIN
                  ]) ? (
                    <SidebarItem
                      icon={<FontAwesomeIcon icon={faExclamationCircle} />}
                      label="Vencimientos Flota"
                      isSelected={
                        selectedItem === '/main/operations/gdf/fleet-expirations'
                      }
                      redirectTo="/main/operations/gdf/fleet-expirations"
                      isCollapsed={isCollapsed}
                    />
                  ) : null}
                  {hasAnyRole([
                    UserRole.RESPONSABLE_CHOFERES,
                    UserRole.ANALISTA_CUENTAS_A_PAGAR,
                    UserRole.GERENTE_ADMINISTRACION,
                    UserRole.SUPER_ADMIN
                  ]) ? (
                    <SidebarItem
                      icon={<FontAwesomeIcon icon={faHandshake} size="1x" />}
                      label="Facturadores"
                      isSelected={selectedItem === '/main/operations/gdf/billers'}
                      redirectTo="/main/operations/gdf/billers"
                      isCollapsed={isCollapsed}
                    />
                  ) : null}
                  {hasAnyRole([
                    UserRole.RESPONSABLE_CHOFERES,
                    UserRole.COORDINADOR,
                    UserRole.GERENTE_OPERACIONES,
                    UserRole.SUPERVISOR_COORDINACION,
                    UserRole.GERENTE_OPERACIONES,
                    UserRole.GERENTE_COMERCIAL,
                    UserRole.SUPER_ADMIN
                  ]) ? (
                    <SidebarItem
                      icon={<FontAwesomeIcon icon={faMoneyCheck} size="1x" />}
                      label="Promedio choferes"
                      isSelected={
                        selectedItem === '/main/operations/driver-operational-information/driver-averages'
                      }
                      redirectTo="/main/operations/driver-operational-information/driver-averages"
                      isCollapsed={isCollapsed}
                    />
                  ) : null}
                </SidebarItem>


                {hasAnyRole([UserRole.SUPER_ADMIN]) ? (
                  <SidebarItem
                    icon={<FontAwesomeIcon icon={faTrash} size="1x" />}
                    label="Eliminar Reserva"
                    isSelected={selectedItem === '/main/operations/delete-reservation'}
                    redirectTo="/main/operations/delete-reservation"
                    isCollapsed={isCollapsed}
                  />
                ) : null}
              </SidebarItem>
            ) : null}


            {/* ADMINISTRACIÓN */}
            {hasAnyRole([
              UserRole.GERENTE_ADMINISTRACION,
              UserRole.ANALISTA_FACTURACION,
              UserRole.SUPER_ADMIN,
              UserRole.RESPONSABLE_COBRANZA,
              UserRole.GERENTE_COMERCIAL,
              UserRole.ANALISTA_COMERCIAL,
              UserRole.ANALISTA_CUENTAS_A_PAGAR,
              UserRole.OPERADOR_CALL_CENTER,
              UserRole.COORDINADOR,
              UserRole.GERENTE_OPERACIONES,
              UserRole.SUPERVISOR_COORDINACION,
            ]) ? (
              <SidebarItem
                label="ADMINISTRACIÓN"
                fontSize={16}
                abbreviationLabel="ADMIN..."
                isCollapsed={isCollapsed}
                noneIcon={true}
              >
                <SidebarItem
                  label="Administración → Facturación / Revisión de viajes"
                  abbreviationLabel="F / R.V."
                  isCollapsed={isCollapsed}
                >
                  {hasAnyRole([
                    UserRole.GERENTE_ADMINISTRACION,
                    UserRole.ANALISTA_FACTURACION,
                    UserRole.SUPER_ADMIN
                  ]) ? (
                    <SidebarItem
                      icon={<FontAwesomeIcon icon={faSearch} size="1x" />}
                      label="Auditoría Corporativo"
                      isSelected={
                        selectedItem === '/main/administration/billing-travel-review/corporate-audit'
                      }
                      redirectTo="/main/administration/billing-travel-review/corporate-audit"
                      isCollapsed={isCollapsed}
                    />
                  ) : null}
                </SidebarItem>
                <SidebarItem
                  label="Correcciones/Facturación"
                  abbreviationLabel="C./F."
                  isCollapsed={isCollapsed}
                >
                  {hasAnyRole([UserRole.SUPER_ADMIN]) ? (
                    <SidebarItem
                      icon={<FontAwesomeIcon icon={faToolbox} size="1x" />}
                      label="Corregir Corporativo"
                      isSelected={
                        selectedItem === '/main/administration/fix-billing/fix-corporate'
                      }
                      redirectTo="/main/administration/fix-billing/fix-corporate"
                      isCollapsed={isCollapsed}
                    />
                  ) : null}
                  {hasAnyRole([UserRole.SUPER_ADMIN]) ? (
                    <SidebarItem
                      icon={<FontAwesomeIcon icon={faToolbox} size="1x" />}
                      label="Corregir Particular"
                      isSelected={
                        selectedItem === '/main/administration/fix-billing/fix-particular'
                      }
                      redirectTo="/main/administration/fix-billing/fix-particular"
                      isCollapsed={isCollapsed}
                    />
                  ) : null}
                </SidebarItem>
                <SidebarItem
                  label="Facturación"
                  abbreviationLabel="F."
                  isCollapsed={isCollapsed}
                >
                  {hasAnyRole([
                    UserRole.GERENTE_ADMINISTRACION,
                    UserRole.ANALISTA_FACTURACION,
                    UserRole.SUPER_ADMIN
                  ]) ? (
                    <SidebarItem
                      icon={<FontAwesomeIcon icon={faMoneyBill} size="1x" />}
                      label="A Liquidar Servicios Corporativos"
                      isSelected={
                        selectedItem ===
                        '/main/administration/billing/pending-corporate-settlements'
                      }
                      redirectTo="/main/administration/billing/pending-corporate-settlements"
                      isCollapsed={isCollapsed}
                    />
                  ) : null}
                  {hasAnyRole([
                    UserRole.GERENTE_ADMINISTRACION,
                    UserRole.RESPONSABLE_COBRANZA,
                    UserRole.ANALISTA_FACTURACION,
                    UserRole.SUPER_ADMIN
                  ]) ? (
                    <SidebarItem
                      icon={<FontAwesomeIcon icon={faCoins} size="1x" />}
                      label="Liquidaciones Corporativas"
                      isSelected={
                        selectedItem ===
                        '/main/administration/billing/corporate-settlements'
                      }
                      redirectTo="/main/administration/billing/corporate-settlements"
                      isCollapsed={isCollapsed}
                    />
                  ) : null}

                  {hasAnyRole([
                    UserRole.GERENTE_ADMINISTRACION,
                    UserRole.RESPONSABLE_COBRANZA,
                    UserRole.ANALISTA_FACTURACION,
                    UserRole.GERENTE_COMERCIAL,
                    UserRole.ANALISTA_COMERCIAL,
                    UserRole.SUPER_ADMIN
                  ]) ? (
                    <SidebarItem
                      icon={
                        <FontAwesomeIcon icon={faFileInvoiceDollar} size="1x" />
                      }
                      label="Servicios Corporativos Facturados"
                      isSelected={
                        selectedItem ===
                        '/main/administration/billing/billed-corporate-services'
                      }
                      redirectTo="/main/administration/billing/billed-corporate-services"
                      isCollapsed={isCollapsed}
                    />
                  ) : null}
                </SidebarItem>
                <SidebarItem
                  label="Liquidación Choferes"
                  abbreviationLabel="L.C."
                  isCollapsed={isCollapsed}
                >
                  {hasAnyRole([
                    UserRole.ANALISTA_CUENTAS_A_PAGAR,
                    UserRole.GERENTE_ADMINISTRACION,
                    UserRole.SUPER_ADMIN
                  ]) ? (
                    <>
                      <SidebarItem
                        icon={<FontAwesomeIcon icon={faHandshake} size="1x" />}
                        label="Liquidar Quincena / Choferes a Liquidar"
                        isSelected={
                          selectedItem === '/main/administration/driver-settlements/settle-fortnight-drivers-to-settle'
                        }
                        redirectTo="/main/administration/driver-settlements/settle-fortnight-drivers-to-settle"
                        isCollapsed={isCollapsed}
                      />
                      <SidebarItem
                        icon={<FontAwesomeIcon icon={faList} size="1x" />}
                        label="Historial"
                        isSelected={selectedItem === '/main/administration/driver-settlements/history'}
                        redirectTo="/main/administration/driver-settlements/history"
                        isCollapsed={isCollapsed}
                      />
                    </>
                  ) : null}
                </SidebarItem>
                {hasAnyRole([
                  UserRole.GERENTE_ADMINISTRACION,
                  UserRole.RESPONSABLE_COBRANZA,
                  UserRole.ANALISTA_FACTURACION,
                  UserRole.GERENTE_COMERCIAL,
                  UserRole.ANALISTA_COMERCIAL,
                  UserRole.SUPER_ADMIN
                ]) && (
                    <SidebarItem
                      icon={
                        <div className="flex gap-1">
                          <FontAwesomeIcon icon={faDollarSign} size="sm" />
                          <FontAwesomeIcon icon={faMobileAlt} size="sm" />
                        </div>
                      }
                      label="Cobros App"
                      isSelected={selectedItem === '/main/administration/appPayments'}
                      redirectTo="/main/administration/appPayments"
                      isCollapsed={isCollapsed}
                    />
                  )}

                {hasAnyRole([
                  UserRole.COORDINADOR,
                  UserRole.SUPERVISOR_COORDINACION,
                  UserRole.SUPER_ADMIN
                ]) ? (
                  <SidebarItem
                    icon={<DepartureBoardIcon />}
                    label="Pendientes"
                    isSelected={selectedItem === '/main/operations/coordination/pending-trips'}
                    redirectTo="/main/operations/coordination/pending-trips"
                    isCollapsed={isCollapsed}
                  />
                ) : null}
                {hasAnyRole([
                  UserRole.OPERADOR_CALL_CENTER,
                  UserRole.COORDINADOR,
                  UserRole.GERENTE_OPERACIONES,
                  UserRole.SUPERVISOR_COORDINACION,
                  UserRole.GERENTE_OPERACIONES,
                  UserRole.ANALISTA_CUENTAS_A_PAGAR,
                  UserRole.GERENTE_ADMINISTRACION,
                  UserRole.RESPONSABLE_COBRANZA,
                  UserRole.ANALISTA_FACTURACION,
                  UserRole.GERENTE_COMERCIAL,
                  UserRole.ANALISTA_COMERCIAL,
                  UserRole.SUPER_ADMIN
                ]) ? (
                  <SidebarItem
                    icon={<FontAwesomeIcon icon={faClipboardList} size="1x" />}
                    label="Gestión Integral"
                    isSelected={
                      selectedItem === '/main/operations/cdr/comprehensive-management'
                    }
                    redirectTo="/main/operations/cdr/comprehensive-management"
                    isCollapsed={isCollapsed}
                  />
                ) : null}

                {/* {hasAnyRole([
            UserRole.OPERADOR_CALL_CENTER,
            UserRole.COORDINADOR,
            UserRole.GERENTE_OPERACIONES,
            UserRole.SUPERVISOR_COORDINACION,
            UserRole.GERENTE_OPERACIONES,
            UserRole.ANALISTA_CUENTAS_A_PAGAR,
            UserRole.GERENTE_ADMINISTRACION,
            UserRole.RESPONSABLE_COBRANZA,
            UserRole.ANALISTA_FACTURACION,
            UserRole.GERENTE_COMERCIAL,
            UserRole.ANALISTA_COMERCIAL,
            UserRole.SUPER_ADMIN
          ]) ? (
            <SidebarItem
              icon={<FontAwesomeIcon icon={faClipboardList} size="1x" />}
              label="Gestion Integral"
              isSelected={selectedItem === '/main/Viaje/traslado/gestion'}
              redirectTo="/main/Viaje/traslado/gestion"
              isCollapsed={isCollapsed}
            />
          ) : null} */}
              </SidebarItem>
            ) : null}

            {/* COMERCIAL */}
            {hasAnyRole([
              UserRole.OPERADOR_CALL_CENTER,
              UserRole.COORDINADOR,
              UserRole.SUPERVISOR_COORDINACION,
              UserRole.GERENTE_OPERACIONES,
              UserRole.GERENTE_ADMINISTRACION,
              UserRole.ANALISTA_FACTURACION,
              UserRole.GERENTE_COMERCIAL,
              UserRole.ANALISTA_COMERCIAL,
              UserRole.SUPER_ADMIN
            ]) ? (
              <SidebarItem
                label="COMERCIAL"
                fontSize={16}
                abbreviationLabel="COMER..."
                isCollapsed={isCollapsed}
                noneIcon={true}
              >
                <SidebarItem
                  label="Gestión de Tarifas"
                  abbreviationLabel="G.T."
                  isCollapsed={isCollapsed}
                >
                  {hasAnyRole([
                    UserRole.OPERADOR_CALL_CENTER,
                    UserRole.COORDINADOR,
                    UserRole.SUPERVISOR_COORDINACION,
                    UserRole.GERENTE_OPERACIONES
                  ]) &&
                    !hasAnyRole([
                      UserRole.GERENTE_ADMINISTRACION,
                      UserRole.RESPONSABLE_COBRANZA,
                      UserRole.ANALISTA_FACTURACION,
                      UserRole.GERENTE_COMERCIAL,
                      UserRole.ANALISTA_COMERCIAL,
                      UserRole.SUPER_ADMIN
                    ]) ? null : (
                    <SidebarItem
                      icon={<FontAwesomeIcon icon={faTag} size="1x" />}
                      label="Tarifa"
                      isSelected={selectedItem === '/main/commercial/rate-management/rate'}
                      redirectTo="/main/commercial/rate-management/rate"
                      isCollapsed={isCollapsed}
                    />
                  )}
                </SidebarItem>
                <SidebarItem
                  label="Gestión de Clientes"
                  abbreviationLabel="G.C."
                  isCollapsed={isCollapsed}
                >
                  {hasAnyRole([
                    UserRole.OPERADOR_CALL_CENTER,
                    UserRole.COORDINADOR,
                    UserRole.SUPERVISOR_COORDINACION,
                    UserRole.GERENTE_OPERACIONES
                  ]) &&
                    !hasAnyRole([
                      UserRole.GERENTE_ADMINISTRACION,
                      UserRole.RESPONSABLE_COBRANZA,
                      UserRole.ANALISTA_FACTURACION,
                      UserRole.GERENTE_COMERCIAL,
                      UserRole.ANALISTA_COMERCIAL,
                      UserRole.SUPER_ADMIN
                    ]) ? null : (
                    <SidebarItem
                      icon={<FontAwesomeIcon icon={faUserTie} size="1x" />}
                      label="Empresa"
                      isSelected={selectedItem === '/main/commercial/management-client/business'}
                      redirectTo="/main/commercial/management-client/business"
                      isCollapsed={isCollapsed}
                    />
                  )}
                </SidebarItem>
                {hasAnyRole([
                  UserRole.GERENTE_ADMINISTRACION,
                  UserRole.ANALISTA_FACTURACION,
                  UserRole.GERENTE_COMERCIAL,
                  UserRole.ANALISTA_COMERCIAL,
                  UserRole.SUPER_ADMIN
                ]) ? (
                  <SidebarItem
                    icon={<FontAwesomeIcon icon={faReceipt} size="1x" />}
                    label="Gestión Peajes"
                    isSelected={selectedItem === '/main/tolls-management'}
                    redirectTo="/main/tolls-management"
                    isCollapsed={isCollapsed}
                  />
                ) : null}
              </SidebarItem>
            ) : null}

            {/* ESTADÍSTICAS */}
            {hasAnyRole([
              UserRole.SUPERVISOR_COORDINACION,
              UserRole.GERENTE_OPERACIONES,
              UserRole.GERENTE_ADMINISTRACION,
              UserRole.RESPONSABLE_COBRANZA,
              UserRole.ANALISTA_FACTURACION,
              UserRole.GERENTE_COMERCIAL,
              UserRole.SUPER_ADMIN
            ]) ? (
              <SidebarItem
                label="ESTADÍSTICAS"
                fontSize={16}
                abbreviationLabel="ESTAD..."
                isCollapsed={isCollapsed}
                noneIcon={true}
              >
                <>
                  <SidebarItem
                    icon={<FontAwesomeIcon icon={faChartBar} size="1x" />}
                    label="Agentes CDR / Reservas x Agente"
                    isSelected={
                      selectedItem === '/main/statistics/bookings-per-agent'
                    }
                    redirectTo="/main/statistics/bookings-per-agent"
                    isCollapsed={isCollapsed}
                  />
                  <SidebarItem
                    icon={<FontAwesomeIcon icon={faChartBar} size="1x" />}
                    label="Reservas x día / Ingresos x día"
                    isSelected={
                      selectedItem === '/main/statistics/daily-bookings'
                    }
                    redirectTo="/main/statistics/daily-bookings"
                    isCollapsed={isCollapsed}
                  />
                  <SidebarItem
                    icon={<FontAwesomeIcon icon={faChartBar} size="1x" />}
                    label="Desempeño x Chofer / Análisis x Chofer / Indicadores x Chofer / KPIs x Chofer"
                    isSelected={
                      selectedItem === '/main/statistics/driver-performance'
                    }
                    redirectTo="/main/statistics/driver-performance"
                    isCollapsed={isCollapsed}
                  />
                  <SidebarItem
                    icon={<FontAwesomeIcon icon={faChartBar} size="1x" />}
                    label="Asignados por Coordinador"
                    isSelected={
                      selectedItem === '/main/statistics/assigned-by-coordinator'
                    }
                    redirectTo="/main/statistics/assigned-by-coordinator"
                    isCollapsed={isCollapsed}
                  />
                  <SidebarItem
                    icon={<FontAwesomeIcon icon={faChartBar} size="1x" />}
                    label="Asignados por día"
                    isSelected={
                      selectedItem === '/main/statistics/assigned-per-day'
                    }
                    redirectTo="/main/statistics/assigned-per-day"
                    isCollapsed={isCollapsed}
                  />
                  <SidebarItem
                    icon={<FontAwesomeIcon icon={faChartBar} size="1x" />}
                    label="Por Operador y Cliente"
                    isSelected={
                      selectedItem === '/main/statistics/per-operator-and-client'
                    }
                    redirectTo="/main/statistics/per-operator-and-client"
                    isCollapsed={isCollapsed}
                  />
                  <SidebarItem
                    icon={<FontAwesomeIcon icon={faChartBar} size="1x" />}
                    label="Ranking de clientes / Reservas x Cliente"
                    isSelected={
                      selectedItem === '/main/statistics/customer-ranking'
                    }
                    redirectTo="/main/statistics/customer-ranking"
                    isCollapsed={isCollapsed}
                  />

                  <SidebarItem
                    icon={<FontAwesomeIcon icon={faChartBar} size="1x" />}
                    label="Reservas Auditadas / Viajes Auditados / Servicios Auditados"
                    isSelected={
                      selectedItem === '/main/statistics/audited-services'
                    }
                    redirectTo="/main/statistics/audited-services"
                    isCollapsed={isCollapsed}
                  />

                  <SidebarItem
                    icon={<FontAwesomeIcon icon={faChartBar} size="1x" />}
                    label="Valor de viaje por cliente"
                    isSelected={
                      selectedItem === '/main/statistics/average-value-per-trip'
                    }
                    redirectTo="/main/statistics/average-value-per-trip"
                    isCollapsed={isCollapsed}
                  />
                  <SidebarItem
                    icon={<FontAwesomeIcon icon={faChartBar} size="1x" />}
                    label="Consumo por cliente"
                    isSelected={
                      selectedItem ===
                      '/main/statistics/average-spent-per-client'
                    }
                    redirectTo="/main/statistics/average-spent-per-client"
                    isCollapsed={isCollapsed}
                  />
                  <SidebarItem
                    icon={<FontAwesomeIcon icon={faChartBar} size="1x" />}
                    label="Precios por Cliente"
                    isSelected={
                      selectedItem ===
                      '/main/statistics/price-per-client'
                    }
                    redirectTo="/main/statistics/price-per-client"
                    isCollapsed={isCollapsed}
                  />
                  <SidebarItem
                    icon={<FontAwesomeIcon icon={faChartBar} size="1x" />}
                    label="Analisis comercial"
                    isSelected={
                      selectedItem ===
                      '/main/statistics/analisis-comercial'
                    }
                    redirectTo="/main/statistics/analisis-comercial"
                    isCollapsed={isCollapsed}
                  />
                  <SidebarItem
                    icon={<FontAwesomeIcon icon={faStar} size="1x" />}
                    label="Calificacion de usuarios APP"
                    isSelected={selectedItem === '/main/commercial/app-ratings'}
                    redirectTo="/main/commercial/app-ratings"
                    isCollapsed={isCollapsed}
                  />
                </>
              </SidebarItem>
            ) : null}
            {/* {hasAnyRole([
              UserRole.OPERADOR_CALL_CENTER,
              UserRole.COORDINADOR,
              UserRole.GERENTE_OPERACIONES,
              UserRole.SUPERVISOR_COORDINACION,
              UserRole.RESPONSABLE_CHOFERES,
              UserRole.GERENTE_COMERCIAL,
              UserRole.ANALISTA_COMERCIAL,
              UserRole.SUPER_ADMIN
            ]) ? (
              <SidebarItem
                icon={<FontAwesomeIcon icon={faPauseCircle} size="1x" />}
                label="Pendientes"
                isSelected={selectedItem === '/main/Viaje/traslado/pendiente'}
                redirectTo="/main/Viaje/traslado/pendiente"
                isCollapsed={isCollapsed}
              />
            ) : null} */}
            {/* {hasAnyRole([
              UserRole.OPERADOR_CALL_CENTER,
              UserRole.COORDINADOR,
              UserRole.GERENTE_OPERACIONES,
              UserRole.SUPERVISOR_COORDINACION,
              UserRole.GERENTE_COMERCIAL,
              UserRole.ANALISTA_COMERCIAL,
              UserRole.SUPER_ADMIN
            ]) ? (
              <SidebarItem
                icon={<FontAwesomeIcon icon={faMap} size="1x" />}
                label="Mapa"
                isSelected={selectedItem === '/main/Viaje/traslado/mapa'}
                redirectTo="/main/Viaje/traslado/mapa"
                isCollapsed={isCollapsed}
              />
            ) : null} */}
            {/* {hasAnyRole([
            UserRole.GERENTE_ADMINISTRACION,
            UserRole.ANALISTA_FACTURACION,
            UserRole.SUPER_ADMIN
          ]) ? (
            <SidebarItem
              icon={<FontAwesomeIcon icon={faSearch} size="1x" />}
              label="A Auditar Contado"
              isSelected={
                selectedItem ===
                '/main/Viaje/traslado/pendiente-auditoria-contado'
              }
              redirectTo="/main/Viaje/traslado/pendiente-auditoria-contado"
              isCollapsed={isCollapsed}
            />
          ) : null} */}
            {/* {hasAnyRole([
            UserRole.ANALISTA_CUENTAS_A_PAGAR,
            UserRole.GERENTE_ADMINISTRACION,
            UserRole.SUPER_ADMIN
          ]) ? (
            <SidebarItem
              icon={<FontAwesomeIcon icon={faFileContract} size="1x" />}
              label="Liquidación choferes"
              isCollapsed={isCollapsed}
            >
              <SidebarItem
                icon={<FontAwesomeIcon icon={faList} size="1x" />}
                label="Historial"
                isSelected={selectedItem === '/main/driver/settlements'}
                redirectTo="/main/driver/settlements"
                isCollapsed={isCollapsed}
              />
            </SidebarItem>
          ) : null} */}


            {/* INVENTARIOS */}
            {hasAnyRole([
              UserRole.SUPERVISOR_COORDINACION,
              UserRole.GERENTE_OPERACIONES,
              UserRole.SUPER_ADMIN,
              UserRole.CHOFER,
              UserRole.COORDINADOR,
              UserRole.OPERADOR_CALL_CENTER,
            ]) ? (
              <SidebarItem
                label="INVENTARIOS"
                fontSize={16}
                abbreviationLabel="INVENT..."
                isCollapsed={isCollapsed}
                noneIcon={true}
              >
                {hasAnyRole([
                  UserRole.SUPERVISOR_COORDINACION,
                  UserRole.GERENTE_OPERACIONES,
                  UserRole.SUPER_ADMIN
                ]) ? (
                  <SidebarItem
                    label="Gestión Antenas"
                    abbreviationLabel="G.A."
                    isCollapsed={isCollapsed}
                  >
                    <SidebarItem
                      icon={<FontAwesomeIcon icon={faMapPin} size="1x" />}
                      label="Antena"
                      isSelected={selectedItem === '/main/inventories/antenna-management/antenna'}
                      redirectTo={'/main/inventories/antenna-management/antenna'}
                      isCollapsed={isCollapsed}
                    />
                    <SidebarItem
                      icon={<FontAwesomeIcon icon={faMapPin} size="1x" />}
                      label="Grupos"
                      isSelected={selectedItem === '/main/inventories/antenna-management/groups'}
                      redirectTo={'/main/inventories/antenna-management/groups'}
                      isCollapsed={isCollapsed}
                    />
                  </SidebarItem>
                ) : null}
                {hasAnyRole([UserRole.SUPER_ADMIN]) ? (
                  <SidebarItem
                    icon={<FontAwesomeIcon icon={faUserCircle} size="1x" />}
                    label="Usuarios"
                    isSelected={selectedItem === '/main/inventories/users'}
                    redirectTo="/main/inventories/users"
                    isCollapsed={isCollapsed}
                  />
                ) : null}

                {hasAnyRole([
                  UserRole.SUPERVISOR_COORDINACION,
                  UserRole.GERENTE_OPERACIONES,
                  UserRole.SUPER_ADMIN
                ]) ? (
                  <SidebarItem
                    icon={
                      <div className="flex gap-1">
                        <FontAwesomeIcon icon={faUsers} size="1x" />
                        <FontAwesomeIcon icon={faMobileAlt} size="1x" />
                      </div>
                    }
                    label="Usuarios App"
                    isSelected={selectedItem === '/main/inventories/appUsers'}
                    redirectTo="/main/inventories/appUsers"
                    isCollapsed={isCollapsed}
                  />
                ) : null}
                {/* <SidebarItem
                icon={<FontAwesomeIcon icon={faUser} size="1x" />}
                label="Particular"
                isCollapsed={isCollapsed}
              >
                <SidebarItem
                  icon={<FontAwesomeIcon icon={faList} size="1x" />}
                  label="Listado"
                  isSelected={
                    selectedItem === '/main/clientParticular/listado'
                  }
                  redirectTo="/main/clientParticular/listado"
                  isCollapsed={isCollapsed}
                />

              </SidebarItem> */}
                {hasAnyRole([
                  UserRole.GERENTE_COMERCIAL,
                  UserRole.ANALISTA_COMERCIAL,
                  UserRole.SUPER_ADMIN
                ]) ? (
                  <SidebarItem
                    icon={
                      <div className="flex gap-1">
                        <FontAwesomeIcon icon={faMinus} size="xs" />
                        <FontAwesomeIcon icon={faMoneyBill} size="xs" />
                      </div>
                    }
                    label="Descuentos"
                    isSelected={selectedItem === '/main/inventories/discounts'}
                    redirectTo="/main/inventories/discounts"
                    isCollapsed={isCollapsed}
                  />
                ) : null}




                {hasAnyRole([UserRole.CHOFER]) ? (
                  <>
                    <SidebarItem
                      icon={<FontAwesomeIcon icon={faCar} size="1x" />}
                      label="A Liquidar"
                      isSelected={
                        selectedItem ===
                        '/main/driver/me/travels-pending-settlement'
                      }
                      redirectTo="/main/driver/me/travels-pending-settlement"
                      isCollapsed={isCollapsed}
                    />
                    <SidebarItem
                      icon={<FontAwesomeIcon icon={faList} size="1x" />}
                      label="Liquidaciones"
                      isSelected={selectedItem === '/main/driver/me/settlements'}
                      redirectTo="/main/driver/me/settlements"
                      isCollapsed={isCollapsed}
                    />
                  </>
                ) : null}
                {hasAnyRole([
                  UserRole.COORDINADOR,
                  UserRole.SUPER_ADMIN,
                  UserRole.OPERADOR_CALL_CENTER,
                  UserRole.GERENTE_OPERACIONES,
                  UserRole.SUPERVISOR_COORDINACION
                ]) ? (
                  <>
                    <SidebarItem
                      icon={<FontAwesomeIcon icon={faCar} size="1x" />}
                      label="Control Liquidaciones"
                      isSelected={
                        selectedItem ===
                        '/main/inventories/liquidation-control'
                      }
                      redirectTo="/main/inventories/liquidation-control"
                      isCollapsed={isCollapsed}
                    />
                  </>
                ) : null}
                {hasAnyRole([
                  UserRole.SUPERVISOR_COORDINACION,
                  UserRole.GERENTE_OPERACIONES,
                  UserRole.SUPER_ADMIN
                ]) ? (
                  <SidebarItem
                    label="Gestión Semáforos"
                    abbreviationLabel="G.A."
                    isCollapsed={isCollapsed}
                  >
                    <SidebarItem
                      label="Configuración de Reglas"
                      isSelected={selectedItem === '/main/inventories/traffic-lights-management/traffic-light-rules-configuration'}
                      redirectTo='/main/inventories/traffic-lights-management/traffic-light-rules-configuration'
                      isCollapsed={isCollapsed}
                    />
                  </SidebarItem>
                ) : null}
                {hasAnyRole([
                  UserRole.SUPERVISOR_COORDINACION,
                  UserRole.GERENTE_OPERACIONES,
                  UserRole.SUPER_ADMIN
                ]) ? (
                  <SidebarItem
                    label="Gestión Contingencias"
                    abbreviationLabel="G.C."
                    isCollapsed={isCollapsed}
                  >
                    <SidebarItem
                      label="Control de Contingencias"
                      isSelected={selectedItem === '/main/inventories/contingencies-control'}
                      redirectTo='/main/inventories/contingencies-control'
                      isCollapsed={isCollapsed}
                    />
                  </SidebarItem>
                ) : null}
              </SidebarItem>
            ) : null}
            {/* EVENTOS */}
            {hasAnyRole([
              UserRole.SUPERVISOR_COORDINACION,
              UserRole.GERENTE_OPERACIONES,
              UserRole.SUPER_ADMIN,
              UserRole.COORDINADOR,
            ]) ? (
              <SidebarItem
                label="EVENTOS"
                fontSize={16}
                abbreviationLabel="EVENT..."
                isCollapsed={isCollapsed}
                noneIcon={true}
              >
                <SidebarItem
                  icon={<FontAwesomeIcon icon={faCalendarAlt} size="1x" />}
                  label="Lista de Eventos"
                  isSelected={selectedItem === '/main/inventories/events'}
                  redirectTo="/main/inventories/events"
                  isCollapsed={isCollapsed}
                />
                <SidebarItem
                  icon={<FontAwesomeIcon icon={faCalendarPlus} size="1x" />}
                  label="Crear un Evento"
                  isSelected={
                    selectedItem === '/main/inventories/events/create'
                  }
                  redirectTo="/main/inventories/events/create"
                  isCollapsed={isCollapsed}
                />
              </SidebarItem>
            ) : null}
          </div>
        </div>
        <div
          className="flex items-center justify-center"
          style={{
            background: isDarkTheme
              ? darkColors.sidebarFooterGradient
              : lightColors.sidebarEdgeGradient,
            height: '40px'
          }}
        >
          <button onClick={authService.logout}>
            <SidebarItem
              icon={
                <FontAwesomeIcon
                  icon={faSignOutAlt}
                  size="1x"
                  style={{ transform: 'rotate(180deg)' }}
                />
              }
              label="Salir"
              isSelected={false}
              isCollapsed={isCollapsed}
            />
          </button>
        </div>
      </div>
    </SelectedItemContext.Provider>
  );
};

export default Sidebar;
